import { useCallback, useMemo } from 'react';
import { useStorageSlot } from '@gonfalon/browser-storage/useStorageSlot';

import { AI_CONFIG_SORT_ORDER_CACHE_KEY, ensureSortParam, isDefaultSortOrdering, type SortOrdering } from '../helpers';

export function useCachedAiConfigSortOrdering() {
  const [rawValue, slot] = useStorageSlot(AI_CONFIG_SORT_ORDER_CACHE_KEY, {
    storageType: 'sessionStorage',
    isEnabled: true,
  });

  const cached = useMemo(() => {
    if (!rawValue) {
      return undefined;
    }

    if (isDefaultSortOrdering(rawValue)) {
      return undefined;
    }

    try {
      return ensureSortParam(rawValue);
    } catch {
      return undefined;
    }
  }, [rawValue]);

  const put = useCallback(
    (value?: SortOrdering) => {
      if (!value || isDefaultSortOrdering(value)) {
        slot.remove();
        return;
      }

      try {
        slot.set(ensureSortParam(value));
      } catch {
        slot.remove();
      }
    },
    [slot],
  );

  const value = useMemo(
    () => ({
      value: cached,
      put,
    }),
    [cached, put],
  );

  return value;
}
